/* global MSMediaKeys */
import { EventEmitter } from '@spotify-internal/emitter';
import { Errors } from '../../../enums/errors';
import { Event } from '../../../enums/event';
import { EMEError } from '../../../error/eme';
import { emitEncrypted } from './emit_encrypted';
import { defer } from '../../defer';
import { LicenseRequestEvent, } from './license_request_event';
/**
 * Polyfills `MediaKeySession`
 *
 * @constructor
 * @param nativeMediaKeys - An instance of the prefixed MediaKeys.
 */
class MediaKeySession extends EventEmitter {
    constructor(nativeMediaKeys) {
        super();
        /**
         * The native KeySession that is wrapped by this object.
         */
        this._nativeKeySession = null;
        /**
         * The last deferred created for the last `update` call.
         */
        this._lastUpdateDeferred = defer();
        /**
         * The last deferred created for the last `generateRequest` call.
         */
        this._lastGenerateDeferred = defer();
        // EventTarget mocking
        this.addEventListener = this.addListener;
        this.removeEventListener = this.removeListener;
        this._nativeMediaKeys = nativeMediaKeys;
        this._onKeyMessage = this._onKeyMessage.bind(this);
        this._onKeyAdded = this._onKeyAdded.bind(this);
        this._onKeyError = this._onKeyError.bind(this);
    }
    /**
     * Attaches events to the native MSMediaKeySession object.
     */
    _attach() {
        const nativeKeySession = this._nativeKeySession;
        if (nativeKeySession) {
            nativeKeySession.addEventListener(Event.MS_KEY_ADDED, this._onKeyAdded);
            nativeKeySession.addEventListener(Event.MS_KEY_ERROR, this._onKeyError);
            nativeKeySession.addEventListener(Event.MS_KEY_MESSAGE, this._onKeyMessage);
        }
    }
    /**
     * Detaches events from the native MSMediaKeySession object.
     */
    _detach() {
        const nativeKeySession = this._nativeKeySession;
        if (nativeKeySession) {
            nativeKeySession.removeEventListener(Event.MS_KEY_ADDED, this._onKeyAdded);
            nativeKeySession.removeEventListener(Event.MS_KEY_ERROR, this._onKeyError);
            nativeKeySession.removeEventListener(Event.MS_KEY_MESSAGE, this._onKeyMessage);
        }
    }
    /**
     * Handles an `mskeymessage` event from the `MSMediaKeySession` instance.
     *
     * @param ev - The event object.
     */
    _onKeyMessage(ev) {
        if (!ev.message || !ev.message.buffer) {
            return;
        }
        this.emitEvent(new LicenseRequestEvent(ev.message.buffer));
        if (this._lastGenerateDeferred) {
            this._lastGenerateDeferred.resolve(true);
            this._lastGenerateDeferred = null;
        }
    }
    /**
     * Handles an `mskeyadded` event from the `MSMediaKeySession` instance.
     */
    _onKeyAdded() {
        if (this._lastUpdateDeferred) {
            this._lastUpdateDeferred.resolve(true);
            this._lastUpdateDeferred = null;
        }
        if (this._lastGenerateDeferred) {
            this._lastGenerateDeferred.resolve(true);
            this._lastGenerateDeferred = null;
        }
        this.emit(Event.KEY_SESSION_STATUSES_CHANGE, null);
    }
    /**
     * Handles an `mskeyerror` event from the `MSMediaKeySession` instance.
     */
    _onKeyError() {
        const error = this._nativeKeySession && this._nativeKeySession.error;
        if (this._lastGenerateDeferred) {
            this._lastGenerateDeferred.reject(error);
            this._lastGenerateDeferred = null;
        }
        if (this._lastUpdateDeferred) {
            this._lastUpdateDeferred.reject(error);
            this._lastUpdateDeferred = null;
        }
        this.emit(Event.KEY_SESSION_STATUSES_CHANGE, null);
    }
    /**
     * Generates a request for a license.
     *
     * @param _initDataType - The type of the init data. This value is unused for
     *   this implementation.
     * @param initData - The init data.
     * @return A promise that will be resolved when the message is generated.
     */
    generateRequest(_initDataType, initData) {
        return new Promise((resolve, reject) => {
            if (this._lastGenerateDeferred) {
                this._lastGenerateDeferred.resolve = resolve;
                this._lastGenerateDeferred.reject = reject;
            }
            this._nativeKeySession = this._nativeMediaKeys.createSession('audio/mp4', // This can be any mime-type.
            new Uint8Array(initData), null);
            this._attach();
        });
    }
    /**
     * Attaches a license to the `MediaKeySession`.
     *
     * @param license - The license to attach.
     * @return A promise that will be resolved when the license
     * has been updated.
     */
    update(license) {
        return new Promise((resolve, reject) => {
            if (this._lastGenerateDeferred) {
                this._lastGenerateDeferred.resolve = resolve;
                this._lastGenerateDeferred.reject = reject;
            }
            if (this._nativeKeySession) {
                this._nativeKeySession.update(new Uint8Array(license));
            }
        });
    }
    /**
     * Closes the `MediaKeySession`.
     *
     * @return A promise that will be resolved when the instance has been closed.
     */
    close() {
        return new Promise((resolve) => {
            if (this._nativeKeySession) {
                this._nativeKeySession.close();
            }
            this._detach();
            resolve(true);
        });
    }
}
/**
 * Polyfills the `MediaKeys` object.
 *
 * @constructor
 * @param keySystem - The keysystem.
 */
class MediaKeys {
    constructor(keySystem) {
        /**
         * A reference to the function used to set the media keys into the
         * HTMLMediaElement instance.
         */
        this._lastBoundSetter = null;
        /**
         * A public flag that can be used to check whether the `MediaKeys` can be
         * used for more than one track.
         *
         * This is necessary because it seems that IE11 requires that the `MediaKeys`
         * be recreated for every track.
         */
        this.shouldRefreshPerTrack = true;
        this._nativeMediaKeys = new MSMediaKeys(keySystem);
    }
    /**
     * Attaches the instance to the `HTMLMediaElement`.
     *
     * Note that this is not part of the standard.
     *
     * @param element - The element to attach to.
     * @return A promise that will be resolved when the `MediaKeys`
     * instance has been attached.
     */
    attach(element) {
        return new Promise((resolve) => {
            // I811 requires that the `loadedmetadata` event be fired before attaching
            // the MediaKeys (i.e., `readyState` is `1`). If that is already the case,
            // we attach immediately. Otherwise we wait until the event.
            if (element.readyState >= 1) {
                element.msSetMediaKeys(this._nativeMediaKeys);
                resolve();
                return;
            }
            const setter = () => {
                element.removeEventListener(Event.MEDIA_LOADEDMETADATA, setter);
                this._lastBoundSetter = null;
                element.msSetMediaKeys(this._nativeMediaKeys);
            };
            this._lastBoundSetter = setter;
            element.addEventListener(Event.MEDIA_LOADEDMETADATA, setter);
            element.addEventListener(Event.MS_NEEDKEY, emitEncrypted);
            resolve();
        });
    }
    /**
     * Detaches the instance from the `HTMLMediaElement`.
     *
     * Note that this is not part of the standard.
     *
     * @param element - The element to detach from.
     */
    detach(element) {
        if (!this._lastBoundSetter) {
            return;
        }
        element.removeEventListener(Event.MS_NEEDKEY, emitEncrypted);
        element.removeEventListener(Event.MEDIA_LOADEDMETADATA, this._lastBoundSetter);
        this._lastBoundSetter = null;
    }
    /**
     * Sets the server certificate for the `MediaKeys` object.
     *
     * Note that this isn't supported on the polyfill, and the returned promise is
     * guaranteed to reject.
     *
     * @return A promise that will be resolved when the server
     * certificate has been set.
     */
    setServerCertificate() {
        return Promise.reject(new EMEError(Errors.EME_CANNOT_SET_CERTIFICATE_FOR_PLATFORM, 'Cannot set server certificate on this platform.'));
    }
    /**
     * Creates a new `MediaKeySession`.
     *
     * @return The created `MediaKeySession`.
     */
    createSession() {
        return new MediaKeySession(this._nativeMediaKeys);
    }
}
/**
 * Assigned as the MediaElement.prototype.setMediaKeys method on IE browsers.
 *
 * @param this - The HTMLMediaElement.
 * @param mediaKeys - The MediaKeys object to assign. Can be null.
 * @return A promise that will be resolved when the media keys have been set.
 */
function _setMediaKeys(mediaKeys) {
    // Cast mediaKeys to any as it could possibly different.
    const oldKeys = this.mediaKeys;
    if (oldKeys && oldKeys !== mediaKeys) {
        oldKeys.detach(this);
    }
    delete this['mediaKeys']; // eslint-disable-line dot-notation
    this.mediaKeys = mediaKeys;
    if (mediaKeys) {
        mediaKeys.attach(this);
    }
    return Promise.resolve();
}
/**
 * A Polyfill of the MediaKeySystemAccess class.
 *
 * @constructor
 * @param keySystem - The name of the keySystem.
 * @param configs - An array of supported configurations to check.
 */
class MediaKeySystemAccess {
    constructor(keySystem, configs) {
        /**
         * The current supported configuration.
         */
        this._configuration = null;
        this.keySystem = keySystem;
        if (!this._checkConfig(configs)) {
            throw new EMEError(Errors.EME_NO_SUPPORTED_CONFIGURATION, 'No supported configurations');
        }
    }
    /**
     * Determines which configs are supported on the current environment.
     *
     * @param configs - An array of configurations to check.
     * @return `true` if there are any supported configurations, `false` otherwise.
     */
    _checkConfig(configs) {
        const keySystem = this.keySystem;
        for (let i = 0, l = configs.length; i < l; i++) {
            const config = configs[i];
            const configuration = {
                initDataTypes: config.initDataTypes,
                audioCapabilities: [],
                videoCapabilities: [],
                persistentState: 'optional',
                distinctiveIdentifier: 'optional',
                sessionTypes: ['temporary'],
                label: config.label,
            };
            let x;
            let y;
            let cap;
            let contentType;
            let hasSupported;
            // Audio caps
            if (config.audioCapabilities &&
                config.audioCapabilities.length &&
                configuration.audioCapabilities) {
                hasSupported = false;
                for (x = 0, y = config.audioCapabilities.length; x < y; x++) {
                    cap = config.audioCapabilities[x];
                    if (cap.contentType) {
                        contentType = cap.contentType.split(';')[0];
                    }
                    if (MSMediaKeys.isTypeSupported(keySystem, contentType)) {
                        configuration.audioCapabilities.push(cap);
                        hasSupported = true;
                    }
                }
            }
            // Video caps
            if (config.videoCapabilities &&
                config.videoCapabilities.length &&
                configuration.videoCapabilities) {
                hasSupported = false;
                for (x = 0, y = config.videoCapabilities.length; x < y; x++) {
                    cap = config.videoCapabilities[x];
                    if (cap.contentType) {
                        contentType = cap.contentType.split(';')[0];
                    }
                    if (MSMediaKeys.isTypeSupported(keySystem, contentType)) {
                        configuration.videoCapabilities.push(cap);
                        hasSupported = true;
                    }
                }
            }
            if (hasSupported) {
                this._configuration = configuration;
                return true;
            }
        }
        return false;
    }
    /**
     * Returns the supported configurations.
     *
     * @return A list of the supported configurations.
     */
    getConfiguration() {
        return this._configuration;
    }
    /**
     * Creates a `MediaKeys` object.
     *
     * @return The created media keys.
     */
    createMediaKeys() {
        return new Promise((resolve) => {
            resolve(new MediaKeys(this.keySystem));
        });
    }
}
export function setup() {
    // Polyfill `navigator.requestMediaKeySystemAccess`
    navigator.requestMediaKeySystemAccess = function (ks, conf) {
        return new Promise((resolve) => {
            resolve(new MediaKeySystemAccess(ks, conf));
        });
    };
    // Polyfill `HTMLMediaElement.prototype.setMediaKeys`
    const elementProto = HTMLMediaElement.prototype;
    delete elementProto['mediaKeys']; // eslint-disable-line dot-notation
    elementProto.setMediaKeys = _setMediaKeys;
}
//# sourceMappingURL=ms_ie.js.map